import type { Game, GameEvent } from "./engine";

const events: GameEvent[] = [
  {
    id: "student_complaints",
    title: "Slow Registration",
    sender: "Registrar",
    day: "Day 1",
    kind: "decision",
    eligibleWhen: (s) => s.day === 1,
    body: [
      "Students report registration pages loading 1–3 seconds slower than normal during peak enrollment.",
      "Leadership: \"Just make the front end faster.\"",
      "Possible causes: a slow query, missing indexes, excessive API calls, identity-service latency, network latency, vendor customization, batch conflicts, capacity limits. You don't know which yet.",
    ],
    choices: [
      {
        id: "start_frontend",
        text: "Start front-end optimization immediately",
        effects: [
          { metric: "scheduleConfidence", delta: 5 },
          { metric: "operationalReadiness", delta: -5 },
        ],
        resultText: "Leadership likes the speed. Nobody's confirmed yet that the front end is actually the bottleneck.",
        schedules: [{ eventId: "wrong_layer_diagnosis", eligibleOnDay: 8 }],
      },
      {
        id: "dba_optimize_all",
        text: "Ask the DBA to optimize all database queries",
        effects: [
          { metric: "scheduleConfidence", delta: 3 },
          { metric: "scopeStability", delta: -5 },
        ],
        resultText: "The DBA starts broad tuning work with no baseline to say which queries actually matter.",
        schedules: [{ eventId: "vendor_customization_surprise", eligibleOnDay: 5 }],
      },
      {
        id: "cross_layer_baseline",
        text: "Create a cross-layer performance baseline before changing anything",
        effects: [
          { metric: "scopeStability", delta: 8 },
          { metric: "dataIntegrity", delta: 5 },
          { metric: "controlReadiness", delta: 5 },
          { metric: "scheduleConfidence", delta: -3 },
        ],
        resultText: "Slower to start, but now you'll know which layer — app, database, network, identity — actually owns the problem.",
        schedules: [{ eventId: "baseline_reveals_query", eligibleOnDay: 2 }],
      },
      {
        id: "escalate_vendor",
        text: "Escalate to the vendor immediately",
        effects: [
          { metric: "stakeholderTrust", delta: -5 },
          { metric: "scheduleConfidence", delta: -3 },
        ],
        resultText: "The vendor asks what diagnostics you've already run. You don't have any yet.",
        schedules: [{ eventId: "vendor_pushback", eligibleOnDay: 3 }],
      },
    ],
  },
  {
    id: "baseline_reveals_query",
    title: "The Baseline Finds Something",
    sender: "DBA",
    day: "Day 2",
    kind: "decision",
    eligibleWhen: () => false,
    body: [
      "The baseline shows one query doing a full table scan during peak enrollment — and it lives inside a vendor-customized module, not core code.",
    ],
    choices: [
      {
        id: "fix_index_now",
        text: "Add the missing index and re-test",
        effects: [
          { metric: "operationalReadiness", delta: 10 },
          { metric: "dataIntegrity", delta: 5 },
          { metric: "scheduleConfidence", delta: 5 },
        ],
        resultText: "The index closes the gap in test. Registration pages come back within target.",
      },
      {
        id: "escalate_to_vendor_first",
        text: "Loop the vendor in before touching their customization",
        effects: [
          { metric: "stakeholderTrust", delta: 5 },
          { metric: "scheduleConfidence", delta: -5 },
        ],
        resultText: "Slower, but you're not modifying vendor-owned code without them knowing.",
        schedules: [{ eventId: "vendor_pushback", eligibleOnDay: 4 }],
      },
    ],
  },
  {
    id: "wrong_layer_diagnosis",
    title: "The Problem Didn't Move",
    sender: "Registrar",
    day: "Day 8",
    kind: "risk",
    eligibleWhen: () => false,
    body: [
      "Two weeks of front-end work shipped. Registration is exactly as slow as before. The deadline is closer, and nobody knows why the fix didn't work — because nobody confirmed the front end was the bottleneck in the first place.",
    ],
    choices: [
      {
        id: "run_baseline_now",
        text: "Stop, run the cross-layer baseline you skipped",
        effects: [
          { metric: "scopeStability", delta: 5 },
          { metric: "scheduleConfidence", delta: -8 },
          { metric: "stakeholderTrust", delta: -5 },
        ],
        resultText: "It costs you credibility to admit the first two weeks targeted the wrong layer — but now you'll actually find the cause.",
        schedules: [{ eventId: "baseline_reveals_query", eligibleOnDay: 11 }],
      },
      {
        id: "blame_infrastructure",
        text: "Tell leadership it's a network/infrastructure problem",
        effects: [
          { metric: "stakeholderTrust", delta: -15 },
          { metric: "controlReadiness", delta: -5 },
        ],
        resultText: "Network team finds nothing, because it isn't network. The real cause is still unknown, and now two teams distrust your diagnosis.",
      },
    ],
  },
  {
    id: "vendor_customization_surprise",
    title: "The Vendor Pushes Back",
    sender: "Vendor Account Manager",
    day: "Day 5",
    kind: "risk",
    eligibleWhen: () => false,
    body: [
      "The DBA's tuning touches a query inside the vendor's customization layer. Vendor: \"Any change there needs a paid change order — 3-week standard lead time.\"",
    ],
    choices: [
      {
        id: "pay_for_expedite",
        text: "Push for an expedited change order",
        effects: [
          { metric: "scheduleConfidence", delta: 5 },
          { metric: "controlReadiness", delta: -3 },
        ],
        resultText: "The vendor agrees to expedite, skipping some of their normal review — noted, not blocked.",
      },
      {
        id: "wait_standard_leadtime",
        text: "Accept the standard vendor lead time",
        effects: [
          { metric: "scheduleConfidence", delta: -10 },
          { metric: "stakeholderTrust", delta: -5 },
        ],
        resultText: "Three weeks now sits directly on your critical path, and leadership wants to know why.",
      },
      {
        id: "build_inhouse_workaround",
        text: "Build an in-house workaround around the vendor code",
        effects: [
          { metric: "operationalReadiness", delta: -8 },
          { metric: "controlReadiness", delta: -10 },
          { metric: "scheduleConfidence", delta: 5 },
        ],
        resultText: "Faster, but you're now running unsupported logic against a vendor system outside your contract's guardrails.",
      },
    ],
  },
  {
    id: "vendor_pushback",
    title: "Support Agreement Question",
    sender: "Vendor Account Manager",
    day: "Day 3",
    kind: "risk",
    eligibleWhen: () => false,
    body: [
      "\"That customization is outside standard support scope. Touching it without a signed change order can void part of your support agreement.\"",
    ],
    choices: [
      {
        id: "get_sponsor_signoff",
        text: "Get the business sponsor to formally accept the support risk",
        effects: [
          { metric: "controlReadiness", delta: 8 },
          { metric: "stakeholderTrust", delta: 3 },
          { metric: "scheduleConfidence", delta: -3 },
        ],
        resultText: "The risk is now visible and owned by someone with the authority to accept it — not buried in your inbox.",
      },
      {
        id: "proceed_without_signoff",
        text: "Proceed without formal sign-off to save time",
        effects: [
          { metric: "controlReadiness", delta: -15 },
          { metric: "scheduleConfidence", delta: 5 },
        ],
        resultText: "It moves faster. If the vendor relationship sours later, there's no record anyone agreed to the risk.",
      },
    ],
  },
  {
    id: "registration_deadline_approaches",
    title: "A Public Commitment",
    sender: "Leadership",
    day: "Day 10+",
    kind: "risk",
    eligibleWhen: (s) => s.day >= 10 && s.metrics.scheduleConfidence < 80,
    body: [
      "Leadership has publicly committed to having the fix live before the next registration window opens in two weeks. No readiness review happened before that announcement.",
    ],
    choices: [
      {
        id: "push_back_with_data",
        text: "Push back with the baseline data and a realistic timeline",
        effects: [
          { metric: "stakeholderTrust", delta: 5 },
          { metric: "scheduleConfidence", delta: 5 },
          { metric: "controlReadiness", delta: 3 },
        ],
        resultText: "Leadership adjusts messaging once they see the actual dependency chain, instead of just the date.",
      },
      {
        id: "comply_silently",
        text: "Say nothing, absorb the pressure",
        effects: [
          { metric: "scheduleConfidence", delta: -10 },
          { metric: "operationalReadiness", delta: -5 },
        ],
        resultText: "The date holds in public. Underneath it, steps start quietly getting compressed.",
      },
    ],
  },
  {
    id: "test_env_differs_from_prod",
    title: "Test Passed. Prod Might Not.",
    sender: "QA Lead",
    day: "Day 12+",
    kind: "decision",
    eligibleWhen: (s) => s.day >= 12,
    body: [
      "The fix passes in test. But test data volume is a fraction of production, and the vendor's customization isn't even installed in the test environment.",
    ],
    choices: [
      {
        id: "require_prodlike_test",
        text: "Require a production-like load test before sign-off",
        effects: [
          { metric: "dataIntegrity", delta: 8 },
          { metric: "operationalReadiness", delta: 8 },
          { metric: "scheduleConfidence", delta: -5 },
        ],
        resultText: "The load test surfaces two more edge cases while there's still time to fix them.",
      },
      {
        id: "trust_test_results",
        text: "Trust the test results, move to sign-off",
        effects: [
          { metric: "dataIntegrity", delta: -8 },
          { metric: "operationalReadiness", delta: -10 },
        ],
        resultText: "Sign-off proceeds on a test environment that doesn't resemble production.",
      },
    ],
  },
  {
    id: "security_review_service_account",
    title: "A Service Account With Too Much Access",
    sender: "Security",
    day: "Day 13+",
    kind: "risk",
    eligibleWhen: (s) => s.day >= 13,
    body: [
      "Security review finds the batch job behind your fix runs under a service account with far more database permissions than it needs.",
    ],
    choices: [
      {
        id: "fix_permissions_now",
        text: "Scope the service account down before launch",
        effects: [
          { metric: "controlReadiness", delta: 12 },
          { metric: "scheduleConfidence", delta: -4 },
        ],
        resultText: "Least-privilege restored. Security signs off clean.",
      },
      {
        id: "accept_risk_with_signoff",
        text: "Accept the risk for launch, get sponsor sign-off, fix after",
        effects: [
          { metric: "controlReadiness", delta: -5 },
          { metric: "stakeholderTrust", delta: 2 },
          { metric: "scheduleConfidence", delta: 4 },
        ],
        resultText: "Launch stays on track. An over-permissioned service account ships to production with a documented exception.",
      },
    ],
  },
  {
    id: "uat_regression",
    title: "Three Days Before Launch",
    sender: "UAT Team",
    day: "Day 15+",
    kind: "gate",
    eligibleWhen: (s) => s.day >= 15,
    body: [
      "UAT finds a regression: under specific course combinations, the fix creates duplicate seat holds for a small number of students. The launch date is already public.",
    ],
    choices: [
      {
        id: "classify_and_present_options",
        text: "Classify by severity, quantify affected records, present options to the sponsor",
        effects: [
          { metric: "dataIntegrity", delta: 10 },
          { metric: "stakeholderTrust", delta: 8 },
          { metric: "controlReadiness", delta: 5 },
          { metric: "scheduleConfidence", delta: -5 },
        ],
        resultText: "The sponsor still owns the launch decision — but now makes it with severity, affected-record count, and a remediation estimate in front of them.",
      },
      {
        id: "launch_as_planned",
        text: "Launch as planned — the sponsor owns the date",
        effects: [
          { metric: "dataIntegrity", delta: -15 },
          { metric: "operationalReadiness", delta: -10 },
          { metric: "stakeholderTrust", delta: -5 },
        ],
        resultText: "A data-integrity defect ships without the sponsor ever seeing it named as one.",
      },
      {
        id: "let_tech_lead_decide",
        text: "Let the technical lead make the call",
        effects: [
          { metric: "controlReadiness", delta: -8 },
          { metric: "stakeholderTrust", delta: -5 },
        ],
        resultText: "A business risk decision gets made by someone with no authority over the business outcome.",
      },
    ],
  },
  {
    id: "go_no_go",
    title: "Go / No-Go",
    sender: "Steering Committee",
    day: "Day 16+",
    kind: "gate",
    eligibleWhen: (s) => s.resolvedEventIds.includes("uat_regression") && s.day >= 16,
    body: ["Final go/no-go meeting before production deployment."],
    choices: [
      {
        id: "conditional_launch",
        text: "Conditional launch — rollback plan ready, monitoring on the fixed query",
        effects: [
          { metric: "operationalReadiness", delta: 8 },
          { metric: "scheduleConfidence", delta: 3 },
          { metric: "controlReadiness", delta: 3 },
        ],
        resultText: "Launch proceeds with a safety net in place, not just a hope.",
      },
      {
        id: "full_delay",
        text: "Delay launch until every open item is closed",
        effects: [
          { metric: "scheduleConfidence", delta: -10 },
          { metric: "stakeholderTrust", delta: -3 },
          { metric: "dataIntegrity", delta: 5 },
        ],
        resultText: "The date slips, but nothing ships half-verified.",
      },
      {
        id: "launch_no_monitoring",
        text: "Launch without extra monitoring, to save effort",
        effects: [{ metric: "operationalReadiness", delta: -12 }],
        resultText: "Launch proceeds with no better visibility into production than before.",
      },
    ],
  },
  {
    id: "hypercare_incident",
    title: "Hypercare: It's Happening Again",
    sender: "On-Call Engineer",
    day: "Post Go-Live",
    kind: "risk",
    eligibleWhen: (s) =>
      s.resolvedEventIds.includes("go_no_go") &&
      (s.metrics.operationalReadiness <= 65 || s.metrics.dataIntegrity <= 65),
    body: [
      "During hypercare week: registration is slow again for a subset of students, and a handful now show duplicate seat holds.",
    ],
    choices: [
      {
        id: "activate_rollback",
        text: "Activate the rollback plan",
        effects: [
          { metric: "operationalReadiness", delta: 10 },
          { metric: "stakeholderTrust", delta: 3 },
          { metric: "scheduleConfidence", delta: -8 },
        ],
        resultText: "Rollback works because there was a plan and a tested path back.",
      },
      {
        id: "patch_in_prod",
        text: "Patch directly in production under pressure",
        effects: [
          { metric: "operationalReadiness", delta: -5 },
          { metric: "controlReadiness", delta: -10 },
        ],
        resultText: "The patch goes in fast, unreviewed, directly against production.",
      },
    ],
  },
  {
    id: "lessons_learned",
    title: "Lessons Learned",
    sender: "Director",
    day: "Post-Launch",
    kind: "decision",
    eligibleWhen: (s) => s.resolvedEventIds.includes("go_no_go") && s.day >= 17,
    body: [
      "Registration is live. Before this closes out, what carries forward to the next platform incident?",
    ],
    choices: [
      {
        id: "document_root_cause",
        text: "Document the actual root cause and diagnostic path for next time",
        effects: [
          { metric: "scopeStability", delta: 10 },
          { metric: "controlReadiness", delta: 5 },
        ],
        resultText: "The next 'just make it faster' request starts from a baseline playbook instead of a guess.",
      },
      {
        id: "close_and_move_on",
        text: "Close the ticket and move on",
        effects: [{ metric: "scopeStability", delta: -5 }],
        resultText: "Nothing is documented. The next performance complaint starts from zero again.",
      },
    ],
  },
];

function averageMetric(metrics: Record<string, number>): number {
  const values = Object.values(metrics);
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export const registrationPerformanceGame: Game = {
  title: "Root Cause: Banner Registration Performance",
  premise: [
    "You're the Systems Analyst / PM called in when students report slow registration during peak enrollment.",
    "The problem could live in the application, the database, the network, identity, or a vendor customization — you don't know which yet.",
    "Six readiness metrics track the project. There's rarely one clean right answer, only tradeoffs and what they cost later.",
  ],
  initialMetrics: {
    scopeStability: 100,
    scheduleConfidence: 100,
    dataIntegrity: 100,
    stakeholderTrust: 100,
    controlReadiness: 100,
    operationalReadiness: 100,
  },
  events,
  endWhen: (s) => s.resolvedEventIds.includes("lessons_learned") || s.day > 25,
  outcomes: [
    {
      id: "strong_delivery",
      label: "Root Cause Found, Trust Intact",
      when: (s) => Object.values(s.metrics).every((v) => v >= 65),
      summary:
        "You found the real bottleneck instead of guessing, kept controls visible instead of quietly skipped, and leadership trusts the next estimate you give them.",
    },
    {
      id: "recovered",
      label: "Recovered After a Rocky Start",
      when: (s) => averageMetric(s.metrics) >= 55,
      summary:
        "It wasn't clean — some early guesses cost time or trust — but the project recovered enough to land safely. The next incident will go faster because of what got documented here.",
    },
    {
      id: "rough_landing",
      label: "Live, But It Cost You",
      when: () => true,
      summary:
        "Registration is live, but real damage was done along the way — to data integrity, to trust, or to production stability. The next platform incident starts from a weaker position.",
    },
  ],
};
